import { useMemo } from "react";
import { CheckboxFilterOption } from "@/shared/components/filter/CheckboxFilterGroup";
import { PathPickFilterGroup } from "@/features/pathpick/components/ui/PathPickFilterPanel";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useMembership from "@/shared/hooks/useMembership";
import usePathPickSyncKinds from "@/features/pathpick/hooks/usePathPickSyncKinds";
import { PathPickMarketSyncKind } from "@/features/pathpick/types";
import usePathPickBasketFilter, {
  PathPickBasketFilter,
  PathPickBasketFilterKey,
} from "@/features/pathpick/features/basket/hooks/usePathPickBasketFilter";
import useGetPathPickBasketFilterInfo from "@/features/pathpick/features/basket/hooks/query/useGetPathPickBasketFilterInfo";
import { toPathPickBrandOptions } from "@/features/pathpick/utils";
import { PathPickBasketSoldOutFilter } from "@/features/pathpick/features/basket/types";
import { BASKET_SOLD_OUT_ALL_VALUE } from "@/features/pathpick/features/basket/constants";

// 패널 체크 상태(드래프트)와 칩(적용값)이 같은 규칙으로 값을 읽는다.
// 품절 필터는 스칼라이고, 전체(값 없음)는 빈 배열로 내려 배지/칩에서 제외한다.
export const getPathPickBasketFilterValues = (
  source: PathPickBasketFilter,
  key: PathPickBasketFilterKey
): (string | number)[] => {
  if (key === "soldOut") {
    return source.soldOut ? [source.soldOut] : [];
  }
  return (source[key] ?? []) as (string | number)[];
};

// 그룹 체크 토글 → 드래프트 패치. 드래프트를 클로저로 읽지 않도록 updateDraft에 넘겨 쓴다.
// 품절 필터는 단일 선택이라 토글이 아니라 교체이고, `전체`는 값을 비운다.
export const togglePathPickBasketFilterValue = (
  current: PathPickBasketFilter,
  key: PathPickBasketFilterKey,
  value: string | number
): Partial<PathPickBasketFilter> => {
  if (key === "soldOut") {
    return {
      soldOut:
        value === BASKET_SOLD_OUT_ALL_VALUE
          ? undefined
          : (value as PathPickBasketSoldOutFilter),
    };
  }
  const values = getPathPickBasketFilterValues(current, key);
  return {
    [key]: values.includes(value)
      ? values.filter((item) => item !== value)
      : [...values, value],
  };
};

const SYNC_CHANNEL_FILTER_LABEL_KEYS: Record<PathPickMarketSyncKind, string> = {
  NAVER_SYNC: "path_pick:basket.list.filter_channel_naver_sync",
  TRENBE_SYNC: "path_pick:basket.list.filter_channel_trenbe_sync",
  MUSTIT_SYNC: "path_pick:basket.list.filter_channel_mustit_sync",
  CAFE24_SYNC: "path_pick:basket.list.filter_channel_cafe24_sync",
};

// 담은 상품 필터 그룹 — 품절 상품은 모든 탭 공통이고 나머지는 탭마다 노출 그룹이 다르다.
//   작업 전     : 브랜드 / 카테고리 / 상품 유형(핫딜 권한 있을 때만) / 품절 상품
//   등록 신청한 : 브랜드 / 카테고리 / 등록 채널 / 품절 상품 (처리 상태는 주석 처리)
//   주문한      : 브랜드 / 카테고리 / 품절 상품
// 인라인 필터 패널과 모바일 필터 모달이 같은 그룹을 쓴다.
//
// 옵션·그룹은 고정 참조로 둔다 — 범위 입력으로 드래프트가 바뀔 때 memo된 그룹 카드
// (특히 옵션이 많은 브랜드)가 다시 그려지지 않게 하기 위해서다.
const usePathPickBasketFilterGroups = () => {
  const { t } = useI18nHelper();
  const { tab } = usePathPickBasketFilter();
  // 브랜드/카테고리 옵션은 담은 상품 전용 필터 정보다 — 담은 상품에 없는 값은 옵션에 나오지 않는다.
  const { pathPickBasketFilterInfo } = useGetPathPickBasketFilterInfo();
  const { showHotDeal, isApiSync } = useMembership();
  // 네이버 외 마켓 연동 권한이 있는 바이어는 그 채널로도 거를 수 있다.
  const syncKinds = usePathPickSyncKinds();

  const brandOptions = useMemo<CheckboxFilterOption[]>(
    () =>
      toPathPickBrandOptions(pathPickBasketFilterInfo?.brands).map((brand) => ({
        label: brand.name,
        value: brand.id,
      })),
    [pathPickBasketFilterInfo?.brands]
  );

  const categoryOptions = useMemo<CheckboxFilterOption[]>(
    () =>
      (pathPickBasketFilterInfo?.categories ?? []).map((category) => ({
        label: category,
        value: category,
      })),
    [pathPickBasketFilterInfo?.categories]
  );

  const productTypeOptions = useMemo<CheckboxFilterOption[]>(
    () => [
      { label: t("path_pick:filter.product_type_product"), value: "PRODUCT" },
      {
        label: t("path_pick:filter.product_type_line_sheet"),
        value: "LINE_SHEET_PRODUCT",
      },
    ],
    [t]
  );

  // 처리 상태 섹션 주석 처리 — 복원 시 함께 해제
  // const requestStatusOptions: CheckboxFilterOption[] = [
  //   {
  //     label: t("path_pick:basket.list.filter_request_status_completed"),
  //     value: "completed",
  //   },
  //   {
  //     label: t("path_pick:basket.list.filter_request_status_needs_check"),
  //     value: "needsCheck",
  //   },
  // ];

  // 품절 상품은 단일 선택이고 `전체`는 미적용(값 없음)을 뜻한다.
  const soldOutOptions = useMemo<CheckboxFilterOption[]>(
    () => [
      {
        label: t("path_pick:basket.list.filter_sold_out_all"),
        value: BASKET_SOLD_OUT_ALL_VALUE,
      },
      {
        label: t("path_pick:basket.list.filter_sold_out_exclude"),
        value: "EXCLUDE",
      },
      {
        label: t("path_pick:basket.list.filter_sold_out_only"),
        value: "ONLY",
      },
    ],
    [t]
  );

  // API 연동 권한이 없는 멤버십은 파일 생성만 신청할 수 있어 연동 채널 옵션을 노출하지 않는다.
  const requestChannelOptions = useMemo<CheckboxFilterOption[]>(
    () => [
      ...(isApiSync
        ? [
            // 네이버 + 권한 있는 마켓(표시 순서는 PATH_PICK_MARKET_SYNC_KINDS).
            ...syncKinds.map((kind) => ({
              label: t(SYNC_CHANNEL_FILTER_LABEL_KEYS[kind]),
              value: kind,
            })),
          ]
        : []),
      {
        label: t("path_pick:basket.list.filter_channel_naver_excel"),
        value: "NAVER_EXCEL",
      },
      {
        label: t("path_pick:basket.list.filter_channel_kream_excel"),
        value: "KREAM_EXCEL",
      },
    ],
    [t, isApiSync, syncKinds]
  );

  return useMemo<PathPickFilterGroup<PathPickBasketFilterKey>[]>(
    () => [
      {
        key: "brandId__in",
        title: t("path_pick:filter.brand"),
        options: brandOptions,
      },
      {
        key: "pathradeCategory__in",
        title: t("path_pick:filter.category"),
        options: categoryOptions,
      },
      // 핫딜 권한이 없는 멤버십에는 핫딜 상품 자체가 노출되지 않아 상품 유형 구분이 의미 없다.
      ...(tab === "pending" && showHotDeal
        ? [
            {
              key: "productType__in" as const,
              title: t("path_pick:filter.product_type"),
              options: productTypeOptions,
            },
          ]
        : []),
      ...(tab === "requested"
        ? [
            // 처리 상태 섹션 주석 처리 — 복원 시 requestStatusOptions와 함께 해제
            // {
            //   key: "requestStatus__in" as const,
            //   title: t("path_pick:basket.list.filter_request_status"),
            //   options: requestStatusOptions,
            // },
            {
              key: "requestChannel__in" as const,
              title: t("path_pick:basket.list.filter_channel"),
              options: requestChannelOptions,
            },
          ]
        : []),
      {
        key: "soldOut" as const,
        title: t("path_pick:basket.list.filter_sold_out"),
        options: soldOutOptions,
        single: { allValue: BASKET_SOLD_OUT_ALL_VALUE },
      },
    ],
    [
      t,
      tab,
      showHotDeal,
      brandOptions,
      categoryOptions,
      productTypeOptions,
      requestChannelOptions,
      soldOutOptions,
    ]
  );
};

export default usePathPickBasketFilterGroups;
